import { useEffect, useState } from "react";
import { endpoints, fmtUSD } from "@/lib/api";
import { Check } from "lucide-react";

const MODES = [
  {
    key: "agent",
    title: "Agent",
    caption:
      "Private Offer Autopilot executes on a registered VAR's AMMP as a delegated user. The VAR remains seller of record.",
  },
  {
    key: "principal",
    title: "Principal",
    caption:
      "Private Offer Autopilot operates as the VAR — used for buyer accounts with no registered channel partner.",
  },
  {
    key: "broker",
    title: "Broker",
    caption:
      "Route the deal to an eligible distributor or VAR; Private Offer Autopilot coordinates but does not transact.",
  },
];

export default function OrderRouting() {
  const [mode, setMode] = useState("agent");
  const [partners, setPartners] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    endpoints.partners().then((r) => setPartners(r.partners));
  }, []);

  return (
    <div className="max-w-[1200px] px-12 py-14" data-testid="page-routing">
      <div className="tx-overline mb-2">Module M3</div>
      <h1 className="font-heading font-black text-4xl mb-2">Order Routing</h1>
      <p className="text-zinc-600 text-[14px] max-w-2xl mb-8">
        Select the operating mode and the reseller (or distributor) that will
        carry the CPPO.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 mb-10 border border-zinc-200">
        {MODES.map((m, i) => (
          <button
            key={m.key}
            data-testid={`mode-${m.key}`}
            onClick={() => setMode(m.key)}
            className={`text-left p-6 transition-colors ${
              i < MODES.length - 1 ? "border-r border-zinc-200" : ""
            } ${
              mode === m.key
                ? "bg-orange-50 border-b-2 border-b-orange-500"
                : "bg-zinc-50 hover:bg-zinc-100 border-b-2 border-b-transparent"
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span
                className={`font-heading font-black text-2xl ${
                  mode === m.key ? "text-orange-600" : "text-zinc-800"
                }`}
              >
                {m.title}
              </span>
              {mode === m.key && <Check className="w-4 h-4 text-orange-600" />}
            </div>
            <p className="text-[12px] text-zinc-600 leading-relaxed">{m.caption}</p>
          </button>
        ))}
      </div>

      <div className="tx-overline mb-3">Available AWS ACE partners</div>
      <div className="border border-zinc-200 bg-zinc-50">
        <table className="w-full text-[13px]">
          <thead className="border-b border-zinc-200">
            <tr className="text-left">
              {["Partner", "AWS Account", "Status", "Competency", "Regions", "Avg deal", ""].map(
                (h) => (
                  <th key={h} className="tx-overline px-4 py-3 font-normal">
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {partners.map((p) => (
              <tr
                key={p.partner_name}
                onClick={() => setSelected(p.partner_name)}
                data-testid={`partner-row-${p.partner_name}`}
                className={`border-b border-zinc-200 last:border-b-0 cursor-pointer hover:bg-zinc-100 ${
                  selected === p.partner_name ? "bg-zinc-50" : ""
                }`}
              >
                <td className="px-4 py-3 font-medium">{p.partner_name}</td>
                <td className="px-4 py-3 font-mono text-zinc-600">
                  {p.aws_account_id}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`text-[11px] font-mono ${
                      p.cp_status === "Active" ? "text-emerald-600" : "text-amber-600"
                    }`}
                  >
                    {p.cp_status}
                  </span>
                </td>
                <td className="px-4 py-3 text-zinc-700">{p.marketplace_competency}</td>
                <td className="px-4 py-3 font-mono text-[11px] text-zinc-600">
                  {p.authorized_regions.join(", ")}
                </td>
                <td className="px-4 py-3 font-mono">{fmtUSD(p.avg_deal_size_usd)}</td>
                <td className="px-4 py-3">
                  {selected === p.partner_name && (
                    <Check className="w-4 h-4 text-orange-600" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="mt-6 border border-orange-500/40 bg-orange-50 p-5 tx-fade-in">
          <div className="tx-overline mb-1">Selection</div>
          <div className="font-heading text-xl">
            Mode: <span className="text-orange-600">{mode}</span> · Partner:{" "}
            <span className="text-orange-600">{selected}</span>
          </div>
          <p className="text-[12px] text-zinc-600 mt-2">
            Head to the Authorization Designer to lock these choices into a spec.
          </p>
        </div>
      )}
    </div>
  );
}
